import { useId, useState, useEffect, useCallback } from 'react';
import Select from 'react-select';
import cleanFilename from './util.js';
import { Link, useNavigate } from 'react-router-dom';
import BackButton from './BackButton.js';
import usePageSeo from './seo.js';
import SupportLine from './support_line.js';
import './index.css';

const PAGE_TITLE = 'Create links for your JUUGs | MP3JUUG';
const PAGE_DESC =
  'Generate one-time links in MP3JUUG and write them to NFC cards (JUUGs). ' +
  'Fans tap a JUUG to add your music to their library. Create links only after your JUUGs arrive.';

// HowTo + FAQPage structured data — helps search, answer engines, and
// generative engines (SEO/AEO/GEO) quote this page accurately.
const STRUCTURED_DATA = [
  {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: 'How to create links for your JUUGs',
    description: PAGE_DESC,
    step: [
      { '@type': 'HowToStep', position: 1, name: 'Upload your music', text: 'Upload your songs and cover art — we apply the cover art.' },
      { '@type': 'HowToStep', position: 2, name: 'Receive your JUUGs', text: 'Get the physical NFC cards in hand before making any links.' },
      { '@type': 'HowToStep', position: 3, name: 'Pick songs and name the JUUG', text: 'Select the songs for this release and give the JUUG a unique name.' },
      { '@type': 'HowToStep', position: 4, name: 'Choose how many links', text: 'Create one link per physical card. Each link works once.' },
      { '@type': 'HowToStep', position: 5, name: 'Write links to the cards', text: 'Write each link to an NFC card. A fan taps it and the songs are added to their library.' },
    ],
  },
  {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: [
      {
        '@type': 'Question',
        name: 'When should I create links?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Only after your JUUGs (the physical NFC cards) have arrived. Links are one-time use and cannot be paused or reissued, so making them before you can write them to cards wastes them.',
        },
      },
      {
        '@type': 'Question',
        name: 'How many times can a link be used?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'Once. A link is marked used only after the songs are confirmed in the fan’s library, so a failed transfer can be retried.',
        },
      },
      {
        '@type': 'Question',
        name: 'What happens if a download fails?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'The link stays valid. It only burns after the fan’s library confirms the songs were added.',
        },
      },
      {
        '@type': 'Question',
        name: 'Do I need a subscription?',
        acceptedAnswer: {
          '@type': 'Answer',
          text: 'You get 5 free links. Beyond that, MP3JUUG is $5/month for unlimited links.',
        },
      },
    ],
  },
];

function Links(){
    const navigate = useNavigate();
    const [email, setEmail] = useState(null);
    const [checkingAuth, setCheckingAuth] = useState(true);
    const [name, setName] = useState('');
    const [amount, setAmount] = useState('');
    const [links, setLinks] = useState([]);
    const [rawSongs, setRawSongs] = useState([[]]);
    const [pickedSongs, setPickedSongs] = useState([]);
    const [subStatus, setSubStatus] = useState(null);
    const [needsSubscription, setNeedsSubscription] = useState(false);
    const [subMessage, setSubMessage] = useState(null);
    const [billingLoading, setBillingLoading] = useState(false);
    const nameId = useId();
    const amountId = useId();
    const songsLabelId = useId();

    // Page-level SEO/AEO/GEO: descriptive title, meta description, and
    // HowTo + FAQ structured data injected while this route is mounted.
    usePageSeo({ title: PAGE_TITLE, description: PAGE_DESC, jsonLd: STRUCTURED_DATA });

    // WHO'S LOGGED IN
    useEffect(() => {
      (async () => {
        try {
          const res = await fetch('/auth/me', { credentials: 'include' });
          if (!res.ok) {
            navigate('/login', { replace: true });
            return;
          }
          const data = await res.json();
          setEmail(data.email);
        } catch (err) {
          console.error(err);
          navigate('/login', { replace: true });
        } finally {
          setCheckingAuth(false);
        }
      })();
    }, [navigate]);

    const refreshSubStatus = useCallback(async () => {
      try{
        const statusRes = await fetch('/subscription/status', { credentials: 'include' });
        if (statusRes.ok) {
          const statusData = await statusRes.json();
          setSubStatus(statusData);
        }
      } catch(err){console.error(err);}
    }, []);

    // LOAD THIS ACCOUNT'S SONGS ONCE LOGGED IN
    useEffect(() => {
        if (!email) return;
        (async () => {
          try{
            const res = await fetch('/links/songs', {
              method: 'POST',
              headers: {"Content-Type": "application/json"},
              credentials: 'include',
              body: JSON.stringify({}),
            });
            const data = await res.json();
            const cleanSongs = data.songs.map(song => cleanFilename(song));
            setRawSongs(data.songs.map((raw, i) => ({raw, clean: cleanSongs[i]})));
          } catch(err){console.error(err);}
          await refreshSubStatus();
        })();
    }, [email, refreshSubStatus]);

    async function logout() {
      try {
        await fetch('/auth/logout', { method: 'POST', credentials: 'include' });
      } catch (err) {
        console.error(err);
      }
      navigate('/login', { replace: true });
    }

    async function goToCheckout() {
      setBillingLoading(true);
      try {
        const res = await fetch('/subscription/checkout', {
          method: 'POST',
          headers: {"Content-Type": "application/json"},
          credentials: 'include',
          body: JSON.stringify({}),
        });
        const data = await res.json();
        if (!res.ok || !data.url) throw new Error(data.msg || 'Failed to start checkout');
        window.location.href = data.url;
      } catch(err) {
        console.error(err);
        alert(err.message);
        setBillingLoading(false);
      }
    }

    async function goToBillingPortal() {
      setBillingLoading(true);
      try {
        const res = await fetch('/subscription/portal', {
          method: 'POST',
          headers: {"Content-Type": "application/json"},
          credentials: 'include',
          body: JSON.stringify({}),
        });
        const data = await res.json();
        if (!res.ok || !data.url) throw new Error(data.msg || 'Failed to open billing portal');
        window.location.href = data.url;
      } catch(err) {
        console.error(err);
        alert(err.message);
        setBillingLoading(false);
      }
    }

  // FUNCTION THAT SELECTS SONGS FROM UI
  const pickSongs = (selected) => {
    setPickedSongs(selected);
    console.log('songs selected');
   }
   // FUNCTION THAT GETS LINK(S) OF SELECTED SONGS
    async function getLinks(){
        // frontend to display list of links after button click
        const rawSelected = pickedSongs.map(song => song.value); // get songs from frontend to send back for download
        try{
            const res = await fetch('/links/linc',
          {
            method: 'POST',
            headers: {"Content-Type": "application/json"},
            credentials: 'include',
            body: JSON.stringify({
              name,
              amount,
              song: rawSelected})
          });
            const data = await res.json();
            if (res.status === 402) {
              setNeedsSubscription(true);
              setSubMessage(data.msg || null);
              return;
            }
            if(!res.ok) throw new Error(data.msg || 'Links didnt work');
             if (data.exists) {
                alert(`The name "${data.name}" already exists! Choose a different one.`);
                return; // stop here
    }
            setLinks(Array.isArray(data.endpoint) ? data.endpoint : []);
            // amount requested exceeded the free cap: the free portion was
            // still created above, but flag that subscribing gets the rest
            setNeedsSubscription(!!data.requiresSubscription);
            setSubMessage(data.requiresSubscription ? data.msg : null);
            await refreshSubStatus();
        } catch(err) {console.error(err);}
    }

    if (checkingAuth) return null;

    return (
        <main className='page'>
          <div className='page-content'>
            <BackButton fallback='/' />
            <div className='btn-row'>
              <Link to='/juug' className='btn btn-secondary'>See your JUUGS</Link>
              <button type='button' className='btn btn-secondary' onClick={logout}>Log out</button>
            </div>

            <div className='card'>
              <h1>Create links for your JUUGs</h1>
              <p className='lead'>
                A link is a one-time URL you write to an NFC card (a JUUG). When a
                fan taps the card, your songs are added to their library. Make one link per card.
              </p>

              <section aria-labelledby='how-heading'>
                <h2 id='how-heading'>How it works</h2>
                <ol className='steps'>
                  <li>Upload your songs and cover art — we apply the cover art.</li>
                  <li>Wait until your JUUGs arrive. You write links onto the physical cards.</li>
                  <li>Pick the songs for this release and give the JUUG a unique name.</li>
                  <li>Set how many links you need — one per card.</li>
                  <li>Write each link to a card. Tapping it adds the songs to the fan’s library, once.</li>
                </ol>
              </section>

              <div className='notice notice-warn' role='note'>
                <span aria-hidden='true'>⚠️</span>
                <span>
                  <strong>Don’t create links until your JUUGs are in hand.</strong>
                  Every link works exactly once and can’t be paused, edited, or reissued.
                  Links made before you can write them to cards are wasted.
                </span>
              </div>
            </div>

            <div className='card'>
              <h2>Generate links</h2>
              <p className='help-text'>Logged in as {email}</p>
              <form id='downloadForm' onSubmit={(e) => {e.preventDefault(); getLinks()}}>
                <div className='field'>
                  {subStatus && (
                    <p className='help-text'>
                      {subStatus.subscribed
                        ? 'Unlimited links (subscribed)'
                        : `${subStatus.remaining} of ${subStatus.freeLimit} free links remaining`}
                    </p>
                  )}
                </div>

                <div className='field'>
                  <span className='label' id={songsLabelId}>Pick songs</span>
                  {rawSongs.length > 0 && (
                    <Select
                      inputId={`${songsLabelId}-select`}
                      aria-labelledby={songsLabelId}
                      isMulti
                      options={rawSongs.map(song => ({value: song.raw, label: song.clean}))}
                      value={pickedSongs}
                      onChange={pickSongs}
                    />
                  )}
                </div>

                <div className='field'>
                  <label className='label' htmlFor={nameId}>Name your JUUG</label>
                  <input
                    id={nameId}
                    className='input'
                    type='text'
                    onChange={e => setName(e.target.value)}
                  />
                </div>

                <div className='field'>
                  <label className='label' htmlFor={amountId}>Amount of links</label>
                  <input
                    id={amountId}
                    className='input'
                    type='number'
                    inputMode='numeric'
                    min='1'
                    onChange={e => setAmount(e.target.value)}
                  />
                </div>

                <button type='submit' className='btn btn-primary'>Get links</button>
              </form>
            </div>

            {needsSubscription && (
              <div className='card'>
                <h2>{links.length > 0 ? 'More links available with a subscription' : 'Free links used up'}</h2>
                <p>{subMessage || `You've used your ${subStatus?.freeLimit ?? 5} free links. Subscribe for $5/month for unlimited links.`}</p>
                <button type='button' className='btn btn-primary' disabled={billingLoading} onClick={goToCheckout}>
                  {billingLoading ? 'Loading…' : 'Subscribe — $5/month'}
                </button>
              </div>
            )}

            {subStatus?.subscribed && (
              <button type='button' className='btn btn-secondary' disabled={billingLoading} onClick={goToBillingPortal}>
                {billingLoading ? 'Loading…' : 'Manage subscription'}
              </button>
            )}

            <section aria-label='Your links'>
              {Array.isArray(links) && links.length > 0 ? (
                <ul className='list'>
                  {links.map((v, k) => (
                    <li key={k} className='list-item card'>
                      <a
                        href={`http://undergroundradio.us/musicv2/?token=${v}`}
                        target='_blank'
                        rel='noopener noreferrer'
                      >
                        http://undergroundradio.us/musicv2/?token={v}
                      </a>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className='empty-state'>No links yet!</p>
              )}
            </section>

            <section className='card faq' aria-labelledby='faq-heading'>
              <h2 id='faq-heading'>Common questions</h2>
              <details>
                <summary>When should I create links?</summary>
                <p>
                  Only after your JUUGs have arrived. Links are one-time use and can’t be
                  paused or reissued, so making them before you can write them to cards
                  wastes them.
                </p>
              </details>
              <details>
                <summary>How many times can a link be used?</summary>
                <p>
                  Once. A link is marked used only after the songs are confirmed in the
                  fan’s library, so a failed transfer can be retried.
                </p>
              </details>
              <details>
                <summary>What happens if a download fails?</summary>
                <p>The link stays valid. It only burns after the fan’s library confirms the songs were added.</p>
              </details>
              <details>
                <summary>Do I need a subscription?</summary>
                <p>You get 5 free links. Beyond that, MP3JUUG is $5/month for unlimited links.</p>
              </details>
            </section>

            <SupportLine />
          </div>
        </main>
    )
}



export default Links;
